import { Container } from 'unstated'
import { get } from 'lodash'
import clientData from 'utils/client-data'
import { client } from 'utils/client'
import to from 'await-to-js'
import { editSellerProfileSettings } from 'gql/mutation/edit-seller-settings.js'
import { activateProfile } from 'gql/mutation/activate-profile'
import { deactivateProfile } from 'gql/mutation/deactivate-profile'
import { normalizeImage } from '../../utils/image-utils'
import { addLoader, removeLoader } from '../../utils/loader'
import { createSellerProfileMutation } from '../../gql/mutation/create-seller-profile'
import { screenPhoto } from '../../gql/mutation/screen-photo'
import { uploadScannedImageData } from '../../gql/mutation/upload-scanned-photo'

class SellerProfileState extends Container {
  state = {
    businessName: '',
    businessHandle: '',
    businessDescription: '',
    businessLocation: '',
    businessPhone: '',
    businessEmail: '',
    isValidBusinessHandle: false,
    sellerProfileDetails: get(clientData, 'sellerProfileDetails', {}),
    socialMedia: {
      instagramHandle: null,
      facebookUrl: null,
      twitterHandle: null,
      websiteUrl: null,
    },
    contactsOption: {
      phone: null,
      email: null,
      location: null,
    },
    photoSettings: {
      hasSeenDialog: false,
      // profile
      profilePhoto: get(
        clientData,
        'sellerProfileDetails.user_info.profile_photo_url',
        'https://www.paypalobjects.com/paypal-ui/icons/svg/v2-merchant.svg',
      ),
      userHasProfilePicture:
        get(clientData, 'defaultPhotos.profilePhotoUrl') !==
        'https://www.paypalobjects.com/paypal-ui/icons/svg/v2-merchant.svg',
      proposedProfilePhoto: undefined,
      cachedProfilePhoto: undefined,
      cachedDataProfilePhoto: undefined,
      // cover
      initialCoverPhoto: get(
        clientData,
        'sellerProfileDetails.personalization_settings.cover_photo_url',
        'https://www.paypalobjects.com/p2p_seller_profiles/background_covers/blue.jpg',
      ),
      coverPhoto: get(
        clientData,
        'sellerProfileDetails.personalization_settings.cover_photo_url',
        undefined,
      ),
      coverPhotoPan: get(
        clientData,
        'sellerProfileDetails.personalization_settings.cover_photo_vertical_pan',
      ),
      hasCoverPhoto: undefined, // get(clientData, 'sellerProfileDetails.personalization_settings.cover_photo_url') !== 'https://www.paypalobjects.com/p2p_seller_profiles/background_covers/blue.jpg',
      cachedCoverPhoto: undefined,
      proposedCoverPhoto: undefined,
      isCoverDraggable: false,
      suggestedCoverPhotos: get(
        clientData,
        'defaultPhotos.suggestedCoverPhotos',
        [],
      ),
    },
  }

  createSellerProfile = async (onSuccess = () => {}, onError = () => {}) => {
    addLoader()

    // TODO: Edits needed for address and other parameters that maybe passed
    // during seller profile creation
    const variables = {
      businessHandle: this.state.businessHandle,
      businessDescription: this.state.businessDescription,
      businessName: this.state.businessName,
      photoData: {
        profilePhotoUrl: this.state.photoSettings.proposedProfilePhotoUrl,
        coverPhotoUrl: this.state.photoSettings.cachedCoverPhoto,
        coverPhotoVerticalPan: this.state.photoSettings.coverPhotoPan,
      },
      socialData: {
        facebookUrl: this.state.facebookUrl || null,
        instagramUsername: this.state.instagramUsername || null,
        twitterHandle: this.state.twitterHandle || null,
        website: this.state.website || null,
      },
      privacySettings: {
        location: this.state.businessLocation || [],
        displayEmailEnabled: this.state.displayEmailEnabled || false,
        displayMobilePhoneEnabled:
          this.state.displayMobilePhoneEnabled || false,
      },
    }

    try {
      const profile = await client.request(createSellerProfileMutation, {
        input: variables,
      })
      removeLoader()
      onSuccess(profile)
    } catch (e) {
      removeLoader()
      onError(e)
    }
  }

  updateBusinessDetails = (details) => {
    this.setState({
      ...this.state,
      ...details,
    })
  }

  editSellerSettings = async (values, sellerSlugName) => {
    addLoader()

    const [err, response] = await to(
      client.request(editSellerProfileSettings, {
        settings: values,
        slugName: sellerSlugName,
      }),
    )

    if (err) {
      removeLoader()
      throw err
    }

    const {
      editSellerProfileSettings: {
        isProfileStatusActive = false,
        userInfo: {
          alternateFullName = '',
          businessCategory = '',
          displayAddress = '',
          displayEmail = '',
          displayMobilePhone = '',
          instagramUsername = '',
          facebookUrl = '',
          twitterHandle = '',
          website = '',
          profilePhotoUrl = '',
        } = {},
        personalizationSettings: { description = '', coverPhotoUrl = '' } = {},
        privacySettings: {
          emailSearchEnabled = false,
          phoneSearchEnabled = false,
          location = [],
        },
      } = {},
    } = response

    const profileStatus = isProfileStatusActive ? 'ACTIVE' : 'INACTIVE'
    this.setState({
      ...this.state,
      sellerProfileDetails: {
        ...this.state.sellerProfileDetails,
        status: profileStatus,
        user_info: {
          alternate_full_name: alternateFullName,
          business_category: businessCategory,
          display_email: displayEmail,
          display_mobile_phone: displayMobilePhone,
          display_address: displayAddress,
          instagram_username: instagramUsername,
          facebook_url: facebookUrl,
          twitter_handle: twitterHandle,
          website: website,
          profile_photo_url: profilePhotoUrl,
        },
        personalization_settings: {
          cover_photo_url: coverPhotoUrl,
          description,
        },
        privacy_settings: {
          email_search_enabled: emailSearchEnabled,
          phone_search_enabled: phoneSearchEnabled,
          location: location,
        },
      },
    })

    removeLoader()
  }

  turnOnSellerProfile = async (sellerSlugName) => {
    addLoader()

    const [err, response] = await to(
      client.request(activateProfile, {
        slugName: sellerSlugName,
      }),
    )

    if (err) {
      removeLoader()
      throw err
    }

    const { activateProfile: { isProfileStatusActive = false } = {} } = response

    const profileStatus = isProfileStatusActive ? 'ACTIVE' : 'INACTIVE'
    this.setState({
      ...this.state,
      sellerProfileDetails: {
        ...this.state.sellerProfileDetails,
        status: profileStatus,
      },
    })

    removeLoader()
  }

  turnOffSellerProfile = async (sellerSlugName) => {
    addLoader()

    const [err, response] = await to(
      client.request(deactivateProfile, {
        slugName: sellerSlugName,
      }),
    )

    if (err) {
      removeLoader()
      throw err
    }

    const {
      deactivateProfile: { isProfileStatusActive = false } = {},
    } = response

    const profileStatus = isProfileStatusActive ? 'ACTIVE' : 'INACTIVE'
    this.setState({
      ...this.state,
      sellerProfileDetails: {
        ...this.state.sellerProfileDetails,
        status: profileStatus,
      },
    })

    removeLoader()
  }

  setValidBusinessHandle = (flag) => {
    this.setState({
      isValidBusinessHandle: flag,
    })
  }

  // photo upload funcs
  loadCachedProfilePhoto = () => {
    const cachedProfilePhoto = this.state.photoSettings.cachedProfilePhoto
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        proposedProfilePhoto: cachedProfilePhoto,
      },
    })
  }

  updateProfilePhoto = async (
    imageDataUrl,
    onSuccess = () => {},
    onError = () => {},
  ) => {
    const {
      proposedProfilePhoto,
      scannedIdProfilePhoto: scanId,
      profilePhotoHasPassedMediaScreen: hasPassedMediaScreen,
      proposedProfilePhotoFileName: fileName,
    } = this.state.photoSettings

    addLoader()
    // save to media serv
    try {
      const uploadResponse = await client.request(uploadScannedImageData, {
        input: {
          filename: fileName,
          imageDataUrl,
          hasPassedMediaScreen,
          scanId,
        },
      })

      this.setState({
        ...this.state,
        photoSettings: {
          ...this.state.photoSettings,
          cachedDataProfilePhoto: imageDataUrl,
          cachedProfilePhoto: proposedProfilePhoto,
          proposedProfilePhoto: undefined,
          proposedProfilePhotoUrl: get(
            uploadResponse,
            'uploadScannedPhoto.uploadedPhotoUrl',
            undefined,
          ),
        },
      })

      removeLoader()
      onSuccess()
    } catch (e) {
      removeLoader()
      onError()
    }
  }

  updatePhotoSettings = (newSettings) => {
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        ...newSettings,
      },
    })
  }

  // this handles the user clicks 'back' without saving the currently
  // selected file that was 'uploaded' to the browser
  clearProposedProfilePhoto = () => {
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        proposedProfilePhoto: undefined,
      },
    })
  }

  scanProfilePhoto = (
    event,
    onProposed = () => {},
    onError = () => {},
    onServiceError,
  ) => {
    // this loader actually competes with the dialog, there is a loading
    // spinner within the dialog component
    addLoader()
    let fileReader = new FileReader()
    let file
    fileReader.onload = async (onLoadEvent) => {
      let url = onLoadEvent.target.result

      let variables = {
        filename: file.name,
        file: url,
        mediaCategory: 'PROFILE_IMAGE',
      }

      try {
        const response = await client.request(screenPhoto, variables)
        // now to check whether the photo scanned passed or not
        const {
          scanId = '',
          hasPassedMediaScreen = false,
        } = response.screenPhoto

        if (hasPassedMediaScreen) {
          this.setState({
            ...this.state,
            photoSettings: {
              ...this.state.photoSettings,
              proposedProfilePhotoFileName: file.name,
              proposedProfilePhoto: url,
              scannedIdProfilePhoto: scanId,
              profilePhotoHasPassedMediaScreen: hasPassedMediaScreen,
            },
          })
          removeLoader()
          onProposed()
        } else {
          removeLoader()
          onError()
        }
      } catch (e) {
        removeLoader()
        // something went wrong from the service
        if (onServiceError) {
          onServiceError(e)
        } else {
          onError()
        }
      }
    }

    file = event.target.files[0]

    if (file) {
      if (validateImage(file)) {
        fileReader.readAsDataURL(file)
      } else {
        removeLoader()
        onError()
      }
    } else {
      onError()
    }
  }

  // cover photo functions
  pan = (pan, deltaPan = '0', isDragging = false) => {
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        coverPhotoPan: pan,
        deltaPan,
        isDragging,
      },
    })
  }

  setDraggableCoverPhoto = (draggable = false) => {
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        isCoverDraggable: draggable,
      },
    })
  }

  updateCachedCoverPhoto = () => {
    const imageData = this.state.photoSettings.proposedCoverPhoto

    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        cachedCoverPhoto: imageData,
        proposedCoverPhoto: undefined,
        isCoverDraggable: false,
        cachedCoverPan: this.state.photoSettings.coverPhotoPan,
      },
    })
  }

  userImagePreviewUrl = (imagePreviewUrl, filename, isImageDataUrl = false) => {
    this.setState({
      ...this.state,
      photoSettings: {
        ...this.state.photoSettings,
        isCoverDraggable: isImageDataUrl,
        coverFilename: filename,
        ...(!isImageDataUrl && { cachedCoverPhoto: imagePreviewUrl }),
        proposedCoverPhoto: imagePreviewUrl,
      },
    })
  }

  // This function is used by on cover selection, and is a combination of both
  // a call to scan the image for violation AND a call to upload the photo to
  // mediaserv
  loadImage(
    fileSelectEvent,
    onSuccess,
    onImageScanError = () => {},
    onUploadError = () => {},
  ) {
    addLoader()
    let fileReader = new FileReader()
    let file
    fileReader.onload = async (onLoadEvent) => {
      let url = onLoadEvent.target.result
      let options = { mimeType: file.type }

      let variables = {
        filename: file.name,
        file: url,
        mediaCategory: 'COVER_IMAGE',
      }

      try {
        const response = await client.request(screenPhoto, variables)

        const {
          scanId = '',
          hasPassedMediaScreen = false,
        } = response.screenPhoto

        // First check whether it passed the media scan
        if (hasPassedMediaScreen) {
          // Normalize the image before uploading it up to media serv
          normalizeImage(url, 1600, 1600, options, async (normalizedUrl) => {
            try {
              const uploadResponse = await client.request(
                uploadScannedImageData,
                {
                  input: {
                    filename: file.name,
                    imageDataUrl: normalizedUrl,
                    hasPassedMediaScreen,
                    scanId,
                  },
                },
              )

              this.userImagePreviewUrl(
                uploadResponse.uploadScannedPhoto.uploadedPhotoUrl,
                file && file.name,
                true,
              )
              removeLoader()

              if (onSuccess) {
                onSuccess()
              }
            } catch (e) {
              removeLoader()
              onUploadError()
            }
          })
        } else {
          removeLoader()
          onImageScanError()
        }
      } catch (e) {
        removeLoader()
        onImageScanError()
      }
    }

    file = fileSelectEvent.target.files[0]

    if (file) {
      fileReader.readAsDataURL(file)
    }
  }
}

// TODO: There is much more robust validation/manipulation that should be done here.
// Such as modifying the image to "1080p" for smaller upload size similar to cover photo.
function validateImage(image) {
  const MAX_IMAGE_FILE_SIZE = 12000000 // 12 MB
  return !(image.size >= MAX_IMAGE_FILE_SIZE)
}

export default SellerProfileState
